/**
 * AuthContext.jsx
 * Provides authentication state and methods throughout the application.
 * Supports JWT tokens with refresh and secure token storage.
 */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { STORAGE_KEYS, INACTIVITY_TIMEOUT_MS } from '../config/constants';
import { clearAIAnalystSession } from '../utils/aiAnalystSession';
import { clearPortfolioOptions } from '../features/reports/lib/portfolioOptions';
import { clearPortfolioSummary } from '../features/portfolio/lib/portfolioSummary';
import { clearLastAccessedPortfolio } from '../features/portfolio/lib/lastAccessedPortfolio';
import { clearVariableRateBonds } from '../features/manual/lib/variableRateBonds';
import { clearFirmBrandingCache } from '../features/manual/lib/firmBranding';
import { clearMarketplaceSecurities } from '../features/marketplace/lib/marketplaceApi';
import { parseUtcTimestamp } from '../utils/parseUtcTimestamp';

const AuthContext = createContext(null);

// Token refresh threshold (refresh 2 minutes before expiry)
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000;

// Events that count as user activity
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * AuthProvider Component
 * Wraps the application to provide authentication context.
 */
export const AuthProvider = ({ children }) => {
  // Authentication state
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Refresh timer ref
  const refreshTimerRef = useRef(null);
  
  // Inactivity timer ref
  const inactivityTimerRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  // =========================================================================
  // Token Storage Helpers
  // =========================================================================

  /**
   * Store the access token + expiry. The refresh token now lives in an
   * HttpOnly `banyan_refresh` cookie (H5) and is intentionally NOT
   * accessible from JavaScript — the browser attaches it automatically on
   * `/auth/refresh` and `/auth/logout` calls that use `credentials: 'include'`.
   */
  const storeTokens = useCallback((accessToken, expiresAt, rememberMe = true) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    if (expiresAt) {
      storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt);
    }
  }, []);

  /**
   * Clear all stored tokens.
   *
   * H5: also strips any leftover `refresh_token` keys that older builds
   * may have written. The authoritative refresh token now sits in an
   * HttpOnly cookie that `/auth/logout` clears via `Set-Cookie`.
   */
  const clearTokens = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    // Defensive cleanup of legacy keys from before H5.
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    // Drop the cached report picker options so the next user doesn't inherit them.
    clearPortfolioOptions();
    // Drop cached portfolio detail summaries (holdings/PII) so a second user on
    // this tab can't read the previous user's data.
    clearPortfolioSummary();
    // Drop the last-accessed-portfolio autofill pointer (session-scoped) so it
    // can't carry into the next user's session on a shared machine.
    clearLastAccessedPortfolio();
    // Drop the firm-wide variable-rate bonds cache so a second user on this tab
    // can't read the previous user's holdings (data-isolation requirement). The
    // per-account market-value change cache (mvChangeCache) is already cleared by
    // clearPortfolioOptions above.
    clearVariableRateBonds();
    // Same reason for the firm's report branding: it is firm-scoped, so the next
    // user on this tab must fetch their own rather than print the previous firm's.
    clearFirmBrandingCache();
    // Market Place holdings payload (firm-wide securities list).
    clearMarketplaceSecurities();
  }, []);

  /**
   * Get the stored access token + expiry. The refresh token is no longer
   * exposed to JS (H5).
   */
  const getStoredTokens = useCallback(() => {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
                       sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT) ||
                      sessionStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    return { accessToken, expiresAt };
  }, []);

  // =========================================================================
  // Inactivity Timeout Logic
  // =========================================================================

  /**
   * Update last activity timestamp in storage.
   */
  const updateLastActivity = useCallback(() => {
    const now = Date.now().toString();
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, now);
  }, []);

  /**
   * Clear inactivity timer.
   */
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  /**
   * Handle inactivity logout - logs out user and redirects to login.
   */
  const handleInactivityLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    console.log('Session expired due to inactivity');
    
    // Clear all timers
    clearInactivityTimer();
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Clear tokens and state
    clearAIAnalystSession();
    clearTokens();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // Redirect to login page with inactivity message
    window.location.href = '/login?reason=inactivity';
  }, [clearTokens, clearInactivityTimer]);

  /**
   * Start or reset the inactivity timer.
   */
  const resetInactivityTimer = useCallback(() => {
    // Only track inactivity when authenticated
    if (!isAuthenticated) return;
    
    // Update last activity
    updateLastActivity();
    
    // Clear existing timer
    clearInactivityTimer();
    
    // Start new timer
    inactivityTimerRef.current = setTimeout(() => {
      handleInactivityLogout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [isAuthenticated, updateLastActivity, clearInactivityTimer, handleInactivityLogout]);

  /**
   * Manually record activity - call this from components when user interacts
   * (e.g., generating reports, chatbot messages, navigation).
   */
  const recordActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Set up activity event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      clearInactivityTimer();
      return;
    }

    // Throttle activity updates to avoid excessive writes
    let lastUpdate = 0;
    const throttleMs = 60000; // Update at most once per minute

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastUpdate > throttleMs) {
        lastUpdate = now;
        resetInactivityTimer();
      }
    };

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial activity recording
    resetInactivityTimer();

    // Cleanup
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInactivityTimer();
    };
  }, [isAuthenticated, resetInactivityTimer, clearInactivityTimer]);

  // H5: drop the in-memory access token when the tab is closed/reloaded.
  // We deliberately do NOT touch localStorage here — the HttpOnly
  // `banyan_refresh` cookie carries the session across reloads, so on the
  // next mount we either verify the cached access token or call /auth/refresh
  // (which will succeed if the cookie is still valid).
  useEffect(() => {
    const handleBeforeUnload = () => {
      setToken(null);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // =========================================================================
  // Token Refresh Logic
  // =========================================================================

  /**
   * Refresh the access token.
   *
   * H5: the refresh token now lives in the `banyan_refresh` HttpOnly
   * cookie. We use `credentials: 'include'` so the browser attaches the
   * cookie; the request body is empty. CORS on the backend is configured
   * with `allow_credentials=True` and an explicit origin allow-list, and
   * `/auth/refresh` enforces an Origin allow-list check server-side.
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.refresh, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (res.ok) {
        const data = await res.json();
        setToken(data.access_token);

        // Use localStorage by default (matches the "remember me" path); if
        // the previous access token was only in sessionStorage, preserve
        // that scoping so a tab-only login stays tab-only.
        const useLocalStorage = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) !== null
          || sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) === null;
        storeTokens(data.access_token, data.expires_at, useLocalStorage);

        scheduleTokenRefreshRef.current(data.expires_at);

        return true;
      } else {
        clearAIAnalystSession();
        clearTokens();
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [storeTokens, clearTokens]);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const attemptFetch = async (accessToken) => {
      const headers = {
        ...(options.headers || {}),
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      return fetch(url, {
        ...options,
        headers,
      });
    };

    let currentToken = getStoredTokens().accessToken;
    let response = await attemptFetch(currentToken);

    if (response.status !== 401) {
      return response;
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      return response;
    }

    currentToken = getStoredTokens().accessToken;
    return attemptFetch(currentToken);
  }, [getStoredTokens, refreshAccessToken]);

  /**
   * Schedule token refresh before expiry.
   */
  const scheduleTokenRefresh = useCallback((expiresAt) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    if (!expiresAt) return;

    // Parse as UTC: the backend emits offset-less UTC timestamps, which
    // `new Date()` would misread as local time and schedule the refresh hours
    // late for users west of UTC. parseUtcTimestamp also heals stale naive
    // values already in storage from before the backend fix.
    const expiryTime = parseUtcTimestamp(expiresAt);
    const now = Date.now();
    const timeUntilRefresh = expiryTime - now - REFRESH_THRESHOLD_MS;

    if (timeUntilRefresh > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshAccessToken();
      }, timeUntilRefresh);
    } else if (expiryTime > now) {
      // Token still valid but close to expiry - refresh now
      refreshAccessToken();
    }
  }, [refreshAccessToken]);
  
  // Wrap scheduleTokenRefresh reference for useCallback dependency
  const scheduleTokenRefreshRef = useRef(scheduleTokenRefresh);
  useEffect(() => {
    scheduleTokenRefreshRef.current = scheduleTokenRefresh;
  }, [scheduleTokenRefresh]);

  // =========================================================================
  // User Profile
  // =========================================================================

  /**
   * Fetch current user profile from the API.
   */
  const fetchUserProfile = useCallback(async (accessToken) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.me, {
        headers: { 
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
      });

      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      return null;
    }
  }, []);

  // =========================================================================
  // Token Verification on Mount
  // =========================================================================

  useEffect(() => {
    const verifyAndInitialize = async () => {
      const stored = getStoredTokens();
      
      if (stored.accessToken) {
        // Check if user was inactive for too long
        const lastActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
        if (lastActivity) {
          const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
          if (timeSinceActivity >= INACTIVITY_TIMEOUT_MS) {
            // Session expired due to inactivity - clear everything and redirect
            console.log('Session expired due to inactivity on page load');
            clearAIAnalystSession();
            clearTokens();
            localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
            setIsLoading(false);
            window.location.href = '/login?reason=inactivity';
            return;
          }
        }
        
        try {
          // Verify the token
          const res = await fetch(API_ENDPOINTS.auth.verify, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: stored.accessToken }),
          });
          const data = await res.json();
          
          if (data.valid) {
            setToken(stored.accessToken);
            setIsAuthenticated(true);
            
            // Fetch user profile
            await fetchUserProfile(stored.accessToken);
            
            // Schedule token refresh
            scheduleTokenRefresh(stored.expiresAt);
            
            // Initialize last activity if not set
            if (!localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY)) {
              localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
            }
          } else {
            // H5: access token expired/invalid. The refresh token now lives
            // in the HttpOnly cookie, so we always attempt a refresh — the
            // backend will return 401 if the cookie is missing/expired.
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              setIsAuthenticated(true);
              const newToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
                              sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
              await fetchUserProfile(newToken);

              if (!localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY)) {
                localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, Date.now().toString());
              }
            }
          }
        } catch (error) {
          console.error('Token verification failed:', error);
        }
      }
      setIsLoading(false);
    };
    
    verifyAndInitialize();
    
    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [getStoredTokens, fetchUserProfile, scheduleTokenRefresh, refreshAccessToken, clearTokens]);

  // =========================================================================
  // Authentication Methods
  // =========================================================================

  /**
   * Log in with email/username and password.
   * On success the backend emails a 6-digit code; the caller advances to the
   * code-entry step and calls verifyLoginCode. Returns the email to display.
   */
  const login = async ({ email, password }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send/receive the HttpOnly banyan_device cookie: it lets the backend
        // skip 2FA on a trusted device, and a trusted login returns a Set-Cookie
        // (refresh token) the browser must store.
        credentials: 'include',
        body: JSON.stringify({ identifier: email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.detail || 'Login failed' };
      }

      // Trusted device: the backend recognised the banyan_device cookie, skipped
      // the emailed code, and minted the session directly (access token in the
      // body, refresh token in the HttpOnly cookie).
      if (data.access_token) {
        return { success: true, trustedDevice: true, data };
      }

      return { success: true, email: data.email, message: data.message };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  /**
   * Complete login by submitting the emailed 6-digit code. On success the
   * backend mints the session: the access token is in the body and the refresh
   * token rides in the HttpOnly cookie, so we send credentials: 'include' to
   * receive it. Returns the session payload for handleLoginSuccess.
   */
  const verifyLoginCode = async ({ email, code, rememberDevice }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.verifyLoginCode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code, remember_device: !!rememberDevice }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return { success: false, error: data.detail || 'Invalid or expired code' };
      }
      return { success: true, data };
    } catch (error) {
      console.error('Verify login code error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  /**
   * Log out the user.
   */
  const logout = async () => {
    try {
      // H5: refresh token now sits in the HttpOnly `banyan_refresh` cookie.
      // We send `credentials: 'include'` so the browser attaches it; the
      // backend reads the cookie, revokes the token, and emits a Set-Cookie
      // header that clears it. Body is empty.
      if (token) {
        await fetch(API_ENDPOINTS.auth.logout, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({}),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state regardless of API call result
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      clearInactivityTimer();
      clearAIAnalystSession();
      clearTokens();
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  /**
   * Log out from all devices.
   */
  const logoutAll = async () => {
    try {
      if (token) {
        await fetch(API_ENDPOINTS.auth.logoutAll, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          // Send credentials so the browser honors the Set-Cookie that clears
          // banyan_device (the backend revokes the device rows AND clears the
          // cookie on logout-all).
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout all error:', error);
    } finally {
      await logout();
    }
  };

  /**
   * Request password reset code.
   */
  const forgotPassword = async ({ email }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.forgotPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await res.json();
      return res.ok 
        ? { success: true, message: 'Verification code sent to your email!' }
        : { success: false, error: data.detail || 'Failed to send verification code' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  /**
   * Check whether the caller may advance to the reset-password step.
   *
   * H2: the backend's `/auth/verify-reset-code` returns an identical 200
   * response on success and failure to keep code validity indistinguishable
   * over the wire. We treat any 200 as "proceed" and rely on
   * `/auth/reset-password` to be the authoritative validity check. The only
   * non-2xx case we expect here is 429 (rate-limit lockout).
   */
  const verifyResetCode = async ({ email, code }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.verifyResetCode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      if (res.ok) {
        return { success: true, message: 'Proceed to set a new password.' };
      }

      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.detail || 'Verification failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  /**
   * Reset password with code.
   */
  const resetPassword = async ({ email, code, newPassword }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.resetPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      
      const data = await res.json();
      return res.ok
        ? { success: true, message: 'Password reset successful!' }
        : { success: false, error: data.detail || 'Failed to reset password' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  /**
   * Change the signed-in user's password.
   *
   * Sends the bearer access token AND `credentials: 'include'` so the backend
   * can read the `banyan_refresh` cookie: it keeps THIS session's token family
   * alive and revokes the user's other sessions. Returns {success, message} or
   * {success:false, error} so the caller can render success/error states.
   */
  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      const res = await fetch(API_ENDPOINTS.auth.changePassword, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });

      const data = await res.json().catch(() => ({}));
      return res.ok
        ? { success: true, message: data.message || 'Password changed successfully.' }
        : { success: false, error: data.detail || 'Failed to change password' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // =========================================================================
  // Context Value
  // =========================================================================

  const value = {
    // State
    token,
    user,
    isAuthenticated,
    isLoading,
    
    // Auth methods
    login,
    verifyLoginCode,
    logout,
    logoutAll,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    changePassword,

    // Token refresh
    refreshAccessToken,
    fetchWithAuth,
    
    // Activity tracking (call this when user performs important actions)
    recordActivity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access auth context.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;